import mixpanel from "mixpanel-browser";

const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN;
const FIRST_DICTATION_KEY = "zt_mixpanel_first_dictation_completed_v1";
const DICTATION_COMPLETION_COUNT_KEY = "zt_mixpanel_dictation_completion_count_v1";
const SECOND_DICTATION_KEY = "zt_mixpanel_second_dictation_v1";
const MIXPANEL_API_HOST =
  process.env.REACT_APP_MIXPANEL_API_HOST || "https://api-eu.mixpanel.com";
const MIXPANEL_DEBUG = process.env.REACT_APP_MIXPANEL_DEBUG === "true";

let initialized = false;

const isBrowser = () => typeof window !== "undefined";

const normalizePathName = (pathName = "") => {
  const trimmed = String(pathName || "").trim();
  if (!trimmed || trimmed === "/") return "home";
  return trimmed.replace(/^\/+/, "").replace(/\//g, "_");
};

const resolveUserId = (user = {}) =>
  user?.user_id ||
  user?.UserId ||
  user?.id ||
  user?._id ||
  user?.EmailId ||
  user?.email ||
  null;

const resolvePlanType = (user = {}) =>
  user?.plan_type ||
  user?.planType ||
  user?.subscriptionPlan ||
  user?.plan ||
  user?.subscription?.plan ||
  "free";

const resolveSignupMethod = (user = {}) =>
  user?.signup_method ||
  user?.signupMethod ||
  user?.provider ||
  user?.authProvider ||
  "unknown";

export const initMixpanel = () => {
  if (initialized || !isBrowser()) return;
  if (!MIXPANEL_TOKEN) {
    if (MIXPANEL_DEBUG) {
      console.warn("[Mixpanel] Missing REACT_APP_MIXPANEL_TOKEN; init skipped.");
    }
    return;
  }

  const config = {
    track_pageview: false,
    persistence: "localStorage",
    debug: MIXPANEL_DEBUG,
  };
  if (MIXPANEL_API_HOST) {
    config.api_host = MIXPANEL_API_HOST;
  }

  mixpanel.init(MIXPANEL_TOKEN, config);

  mixpanel.register({
    device_type: "web",
    platform: "web",
  });

  if (MIXPANEL_DEBUG) {
    console.info("[Mixpanel] Initialized", {
      token_prefix: String(MIXPANEL_TOKEN).slice(0, 6),
      api_host: MIXPANEL_API_HOST || "default",
    });
  }

  initialized = true;
};

export const trackEvent = (eventName, props = {}) => {
  if (!initialized) return;
  if (MIXPANEL_DEBUG) {
    console.info("[Mixpanel] track", eventName, props);
  }
  mixpanel.track(eventName, {
    device_type: "web",
    ...props,
  });
};

export const trackPageViewed = (pathName, extraProps = {}) => {
  trackEvent("page_viewed", {
    page_name: normalizePathName(pathName),
    path_name: pathName,
    ...extraProps,
  });
};

export const identifyUser = (user = {}) => {
  if (!initialized || !isBrowser()) return;

  const userId = resolveUserId(user);
  if (!userId) return;
  const id = String(userId);

  const aliasKey = `zt_mixpanel_alias_done_${id}`;
  const aliasAlreadyDone = window.localStorage.getItem(aliasKey) === "1";

  if (!aliasAlreadyDone) {
    try {
      mixpanel.alias(id);
      window.localStorage.setItem(aliasKey, "1");
      if (MIXPANEL_DEBUG) {
        console.info("[Mixpanel] alias", id);
      }
    } catch {
      // ignore alias errors to avoid blocking identify
    }
  }

  if (MIXPANEL_DEBUG) {
    console.info("[Mixpanel] identify", id);
  }
  mixpanel.identify(id);
  mixpanel.people.set({
    plan_type: resolvePlanType(user),
    signup_method: resolveSignupMethod(user),
    device_first_used: "web",
    device_type: "web",
  });
};

export const trackFirstDictationCompletedOnce = (props = {}) => {
  if (!initialized || !isBrowser()) return;
  if (window.localStorage.getItem(FIRST_DICTATION_KEY) === "1") return;
  trackEvent("first_dictation_completed", props);
  window.localStorage.setItem(FIRST_DICTATION_KEY, "1");
};

export const trackSecondDictationOnce = (props = {}) => {
  if (!initialized || !isBrowser()) return;

  const currentCount = Number(
    window.localStorage.getItem(DICTATION_COMPLETION_COUNT_KEY) || "0"
  );
  const nextCount = currentCount + 1;
  window.localStorage.setItem(DICTATION_COMPLETION_COUNT_KEY, String(nextCount));

  if (window.localStorage.getItem(SECOND_DICTATION_KEY) === "1") return;
  if (nextCount !== 2) return;

  trackEvent("second_dictation", props);
  window.localStorage.setItem(SECOND_DICTATION_KEY, "1");
};
